/**
 * productResource — the Shopify product catalog, authored against the
 * resource-kit v2 model/view contracts and consumed directly as a
 * `ResourceBundle` by `ResourcePage` (#1805 slice A4a). The
 * three bespoke cell projections — two `render`s and the repo's second
 * `transform` — moved to `./widgets.tsx` as named widgets; none of the three has
 * a v2 carrier on the model field.
 *
 * Two members did NOT survive as declarations:
 *
 *   `filterType: 'date'` on `shipping_date` — `ModelField` has no counterpart.
 *   It moved onto `productShippingDate`'s `Widget.filterType`, which
 *   `toFilterType` consults right after the (now absent) legacy field member.
 *
 *   `placeholder: 'Type'` on `product_type` — dropped, not re-homed, because it
 *   is UNREACHABLE: `placeholder` is read only by form INPUT projections
 *   (`packages/resource-kit-ui/react/registry/field-type-registry.tsx`'s `*Input`),
 *   and this resource declares no
 *   create/update API, so no form ever opens over it. The QueryBuilder does not
 *   read it either. (It is also a hard-coded English string, which the repo's
 *   i18n rule forbids — corroboration, not the reason.)
 */
import {
  type EnumOption,
  createFieldConstructors,
  defineFormView,
  defineListView,
  defineModel,
} from '@resource-kit/model';
import type { ResourceBundle } from '@resource-kit/controllers/react';
import type { Product } from '@/services';
import { productAPI, toListResult, PRODUCT_FILTERS } from '@/services';
import { PRODUCT_SORTS } from '@/services/filters.generated';
import { PERMISSIONS } from '@/permissions';
import i18n from '@/i18n';
import { ProductSeoCard } from './components/ProductSeoCard';

// Mirrors PRODUCT_STATUS in backend/src/contexts/commerce/domain/enums.ts.
// The API contract types `status` as a bare `string`, so nothing generated
// cross-checks this list — it is kept in step by hand, and `unlisted` was
// added here because the backend had to accept it (Shopify API 2025-10+).
const statusOptions: EnumOption[] = [
  { value: 'active', label: 'product.enum.active', color: 'success' },
  { value: 'draft', label: 'product.enum.draft', color: 'secondary' },
  { value: 'unlisted', label: 'product.enum.unlisted', color: 'secondary' },
  { value: 'archived', label: 'product.enum.archived', color: 'destructive' },
];

const f = createFieldConstructors<Product>();

export const productModel = defineModel<Product>()({
  name: 'product',
  idField: 'shopify_id',
  // No runtime consumer on this surface: the detail drawer takes its title from
  // `host.detail.title`, and `recName` is read only where this model is a
  // relation TARGET (`features/relations.ts` → `useReference`), which today
  // registers `supplier` alone. `title` is what names a product everywhere else.
  recName: 'title',
  // #1405: the former `body_html`, `seo_title`, `seo_description`, `source`,
  // `received_at` and `metafields` (raw_data-derived) columns are gone — the
  // `/api/products` wire (generated `Product`) never emitted their backing
  // fields, so they always rendered `undefined` / a dash.
  fields: {
    shopify_id: f.id({ label: 'product.field.shopifyId' }),
    title: f.text({ label: 'product.field.title', widget: 'productTitle' }),
    product_type: f.text({ label: 'product.field.productType' }),
    status: f.enum({ label: 'product.field.status', options: statusOptions }),
    options: f.json({ label: 'product.field.options', widget: 'productOptions' }),
    variants: f.json({ label: 'product.field.variants' }),
    images: f.json({ label: 'product.field.images' }),
    vendor: f.text({ label: 'product.field.vendor' }),
    published_at: f.datetime({ label: 'product.field.publishedAt' }),
    shipping_date: f.text({ label: 'product.field.shippingDate', widget: 'productShippingDate' }),
    created_at: f.datetime({ label: 'product.field.createdAt' }),
    handle: f.text({ label: 'product.field.handle' }),
    tags: f.text({ label: 'product.field.tags' }),
  },
  // The row of the backend's data-change ledger this resource writes to; its
  // only reader is the `ledgerId` gate in
  // `packages/resource-kit-ui/react/form/FormView.tsx`.
  auditResourceType: 'products',
  // A product is ADDRESSED by its Shopify id (`idField` above, and the
  // `/api/products/{shopifyProductId}` path), but the ledger keys its rows on
  // the internal UUID primary key. Without this the history tab queried
  // `resource_id = '<shopify id>'` and came back empty forever — 28 real rows
  // on the sampled product, none of them reachable (#1963). `id` rides the
  // wire already (`backend/src/contexts/commerce/http/product-routes.ts::productSchema`).
  auditResourceIdField: 'id',
});

// The ONE ordered field sequence — see the customers resource for why every
// modifier lives on the list view.
export const productListView = defineListView(productModel, {
  items: [
    { field: 'shopify_id', columnInvisible: true },
    { field: 'title' },
    // Hidden from the list table (#1270): this store's Shopify catalog leaves
    // `product_type` empty on every product, so the column was a full-width
    // strip of dashes. Kept in the detail view for the rare product that does
    // carry a type.
    { field: 'product_type', columnInvisible: true },
    { field: 'status' },
    { field: 'options', columnInvisible: true },
    { field: 'variants', columnInvisible: true },
    { field: 'images', columnInvisible: true },
    { field: 'vendor', columnInvisible: true },
    { field: 'published_at' },
    { field: 'shipping_date', invisible: true },
    { field: 'created_at', columnInvisible: true },
    { field: 'handle', columnInvisible: true },
    { field: 'tags', columnInvisible: true },
  ],
  // `/api/products` really searches (title substring) — see PRODUCT_SEARCH in
  // filters.generated.ts (#1642), bound in BOTH directions by
  // `services/__tests__/resource-searchable-parity.test.ts`.
  capabilities: { search: true },
  defaultSort: { field: 'created_at', dir: 'desc' },
});

export const productFormView = defineFormView(productModel, {
  groupDisplay: 'tab',
  children: [
    // #1922: `shipping_date` used to sit alone in a `shopify_metafields` group.
    // Since #1918 declared the `'tab'` group display, that bought a rail
    // trigger for one scalar field, and the trigger named the field's STORAGE
    // rather than its meaning. The shipping date is the key operational input
    // for dispatch, so it belongs on the overview; that it arrives as a Shopify
    // metafield is already said by the field's own label.
    {
      kind: 'fields',
      id: 'main',
      label: 'product.group.main',
      items: [
        { field: 'title' },
        { field: 'status' },
        { field: 'product_type' },
        { field: 'vendor' },
        { field: 'shipping_date' },
      ],
    },
    {
      kind: 'fields',
      id: 'meta',
      label: 'product.group.meta',
      items: [
        { field: 'created_at' },
        { field: 'published_at' },
        { field: 'handle' },
        { field: 'tags' },
        { field: 'shopify_id' },
      ],
    },
    {
      kind: 'fields',
      id: 'details',
      label: 'product.group.details',
      items: [{ field: 'options' }, { field: 'variants' }, { field: 'images' }],
    },
    {
      kind: 'custom',
      id: 'seo',
      label: 'product.seo.preview',
      render: function ProductSeoTab({ record }) {
        return <ProductSeoCard product={record} />;
      },
    },
    // Declared, not derived (#2783): the kit no longer appends a history tab.
    { kind: 'audit', id: 'audit', label: 'resource.changeHistory' },
  ],
});

export const productResource: ResourceBundle<Product, typeof productModel.fields> = {
  model: productModel,
  list: productListView,
  form: productFormView,
  host: {
    api: {
      // The `/api` `{ items, total, page, size }` page is renamed to the
      // controllers' `ListResult` at this boundary (#2167 PR-2); `size` is the
      // kernel's key and stops here.
      list: async (params) => toListResult(await productAPI.getProducts(params)),
      get: (id) => productAPI.getProduct(id),
    },
    label: 'product.label',
    slug: 'products',
    listTitle: 'product.listTitle',
    listDescription: 'product.listDescription',
    // `products:list` folded into `products:read` (#943 catalog unify).
    permission: { list: PERMISSIONS.PRODUCTS_READ, read: PERMISSIONS.PRODUCTS_READ },
    // Backend filterable whitelist (#contains-filter): status (eq) + title
    // (contains). The QueryBuilder trims to this; other columns are not offered.
    filterable: PRODUCT_FILTERS,
    sortable: PRODUCT_SORTS,
    // L22: NO implicit default filter. The old `status ne archived` default
    // hid archived rows AND the `/api/products` list only supports
    // `status: [eq]` (an `ne` filter is a 400). Dropped so a no-param list
    // returns all products; status is filterable explicitly (opt-in).
    defaultConditions: [],
    staleTime: 5 * 60 * 1000,
    detail: {
      title: (p) => p.title,
      subtitle: (p) => i18n.t('product.detail.subtitle', { id: p.shopify_id }),
    },
  },
};
